"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading } from "@/components/ui/loading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import React, { useEffect, useState } from "react";

interface Company {
  id: string;
  name: string;
  description: string;
  industry: string;
  website: string;
  ownership_type: string;
  ownership_type_other?: string;
}

export default function CompanySettingsPage() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    industry: "",
    website: "",
    ownership_type: "",
    ownership_type_other: "",
    calendly_link: ""
  });
  useEffect(() => {
    async function fetchCompany() {
      if (profile.anonymous) return;

      try {
        // Get company details
        const { data: companyData, error: companyError } = await supabase
          .from("companies")
          .select("*")
          .eq("id", profile.company_id)
          .single();
        if (companyError) {
          console.error("Error fetching company:", companyError);
          setLoading(false);
          return;
        }

        if (!companyData) {
          console.error("Company not found");
          setLoading(false);
          return;
        }

        setCompany(companyData);
        setFormData({
          name: companyData.name || "",
          description: companyData.description || "",
          industry: companyData.industry || "",
          website: companyData.website || "",
          ownership_type: companyData.ownership_type || "",
          ownership_type_other: companyData.ownership_type_other || "",
          calendly_link: companyData.calendly_link || ""
        });
      } catch (error) {
        console.error("Unexpected error:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchCompany();
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (profile.anonymous || !company) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("companies")
        .update(formData)
        .eq("id", company.id);
      if (error) {
        console.error("Error updating company:", error);
        throw error;
      }

      // Refresh company data
      const { data: updatedCompany, error: fetchError } = await supabase
        .from("companies")
        .select("*")
        .eq("id", company.id)
        .single();

      if (fetchError) throw fetchError;
      if (updatedCompany) {
        setCompany(updatedCompany);
        toast({
          title: "Erfolg",
          description: "Die Unternehmensdaten wurden erfolgreich aktualisiert."
        });
      }
    } catch (error) {
      console.error("Error updating company:", error);
      toast({
        title: "Fehler",
        description: "Die Unternehmensdaten konnten nicht aktualisiert werden.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading text="Unternehmensdaten laden..." />;
  }

  if (!company) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-gray-500">Keine Unternehmensdaten gefunden</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Unternehmenseinstellungen</h2>
        <p className="text-sm text-gray-500">
          Verwalte deine Unternehmensinformationen und Einstellungen
        </p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="name" className="text-sm font-medium">
              Firmenname
            </label>
            <Input
              id="name"
              placeholder="Firmennamen eingeben"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="ownership_type" className="text-sm font-medium">
              Trägerschaft
            </label>
            <Select
              value={formData.ownership_type}
              onValueChange={(value: string) =>
                setFormData((prev) => ({ ...prev, ownership_type: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Trägerschaft auswählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="freigemeinnuetzig">
                  Freigemeinnützig
                </SelectItem>
                <SelectItem value="oeffentlich">Öffentlich</SelectItem>
                <SelectItem value="privat">Privat</SelectItem>
                <SelectItem value="other">Sonstige</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.ownership_type === "other" && (
            <div className="space-y-2">
              <label
                htmlFor="ownership_type_other"
                className="text-sm font-medium"
              >
                Sonstige Trägerschaft
              </label>
              <Input
                id="ownership_type_other"
                placeholder="Bitte spezifizieren"
                value={formData.ownership_type_other}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    ownership_type_other: e.target.value
                  }))
                }
              />
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="industry" className="text-sm font-medium">
              Branche
            </label>
            <Select
              value={formData.industry}
              onValueChange={(value: string) =>
                setFormData((prev) => ({ ...prev, industry: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Branche auswählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="technology">Technologie</SelectItem>
                <SelectItem value="healthcare">Gesundheitswesen</SelectItem>
                <SelectItem value="finance">Finanzen</SelectItem>
                <SelectItem value="education">Bildung</SelectItem>
                <SelectItem value="other">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label htmlFor="website" className="text-sm font-medium">
              Website
            </label>
            <Input
              id="website"
              type="url"
              placeholder="https://beispiel.de"
              value={formData.website}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, website: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="website" className="text-sm font-medium">
              Calendly link
            </label>
            <Input
              id="calendly_link"
              type="url"
              placeholder="Your calendly link"
              value={formData.calendly_link}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  calendly_link: e.target.value
                }))
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="description" className="text-sm font-medium">
            Beschreibung
          </label>
          <Textarea
            id="description"
            placeholder="Unternehmensbeschreibung eingeben"
            className="min-h-[200px]"
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
          />
        </div>
      </div>

      <Button type="submit" disabled={saving}>
        {saving ? "Speichern..." : "Änderungen speichern"}
      </Button>
    </form>
  );
}
